// src/store/selectors/branchInstallmentSelectors.js

/**
 * Selectors for branch installment state
 */

// Base selector
export const selectBranchInstallment = (state) => state.branchInstallment;

// Configuration selectors
export const selectInstallmentConfig = (state) =>
  state.branchInstallment.config;

export const selectConfigByBranch = (branchCode) => (state) =>
  state.branchInstallment.configByBranch[branchCode];

export const selectIsInstallmentEnabled = (state) =>
  state.branchInstallment.config?.enabled || false;

export const selectKYCRequirements = (state) =>
  state.branchInstallment.config?.kycRequirements;

export const selectInterestConfig = (state) =>
  state.branchInstallment.config?.interestConfig;

export const selectPaymentRules = (state) =>
  state.branchInstallment.config?.paymentRules;

export const selectPausePolicy = (state) =>
  state.branchInstallment.config?.pausePolicy;

// Plans selectors
export const selectInstallmentPlans = (state) => state.branchInstallment.plans;

export const selectActivePlans = (state) =>
  state.branchInstallment.plans.filter((plan) => plan.isActive);

export const selectPlansByBranch = (branchCode) => (state) =>
  state.branchInstallment.plansByBranch[branchCode] || [];

export const selectActivePlansByBranch = (branchCode) => (state) =>
  (state.branchInstallment.plansByBranch[branchCode] || []).filter(
    (plan) => plan.isActive
  );

export const selectSelectedPlan = (state) =>
  state.branchInstallment.selectedPlan;

export const selectPlanById = (planId) => (state) =>
  state.branchInstallment.plans.find((p) => p._id === planId);

// Analytics selectors
export const selectInstallmentAnalytics = (state) =>
  state.branchInstallment.analytics;

export const selectAnalyticsByBranch = (branchCode) => (state) =>
  state.branchInstallment.analyticsByBranch[branchCode];

export const selectAnalyticsOverview = (state) =>
  state.branchInstallment.analytics?.overview;

export const selectAnalyticsByStatus = (state) =>
  state.branchInstallment.analytics?.byStatus;

export const selectAnalyticsByPlan = (state) =>
  state.branchInstallment.analytics?.byPlan;

export const selectPaymentPerformance = (state) =>
  state.branchInstallment.analytics?.paymentPerformance;

// Loading state selectors
export const selectIsLoadingConfig = (state) =>
  state.branchInstallment.loadingConfig;

export const selectIsLoadingPlans = (state) =>
  state.branchInstallment.loadingPlans;

export const selectIsLoadingAnalytics = (state) =>
  state.branchInstallment.loadingAnalytics;

export const selectIsSavingConfig = (state) =>
  state.branchInstallment.savingConfig;

export const selectIsSavingPlan = (state) => state.branchInstallment.savingPlan;

export const selectIsLoading = (state) =>
  state.branchInstallment.loadingConfig ||
  state.branchInstallment.loadingPlans ||
  state.branchInstallment.loadingAnalytics ||
  state.branchInstallment.savingConfig ||
  state.branchInstallment.savingPlan;

// Error selectors
export const selectInstallmentError = (state) => state.branchInstallment.error;

export const selectConfigError = (state) => state.branchInstallment.configError;

export const selectPlanError = (state) => state.branchInstallment.planError;

export const selectAnalyticsError = (state) =>
  state.branchInstallment.analyticsError;

export const selectValidationErrors = (state) =>
  state.branchInstallment.validationErrors;

export const selectHasErrors = (state) =>
  !!state.branchInstallment.error ||
  !!state.branchInstallment.configError ||
  !!state.branchInstallment.planError ||
  !!state.branchInstallment.analyticsError;

// Edit mode selectors
export const selectIsEditingConfig = (state) =>
  state.branchInstallment.isEditingConfig;

export const selectIsEditingPlan = (state) =>
  state.branchInstallment.isEditingPlan;

// Current context selectors
export const selectCurrentBranchCode = (state) =>
  state.branchInstallment.currentBranchCode;

// Computed selectors
export const selectPlansByTenure = (state) => {
  const plans = state.branchInstallment.plans;
  return plans.reduce((acc, plan) => {
    const tenure = plan.tenureMonths;
    if (!acc[tenure]) {
      acc[tenure] = [];
    }
    acc[tenure].push(plan);
    return acc;
  }, {});
};

export const selectEligiblePlansForAmount = (amount) => (state) =>
  state.branchInstallment.plans.filter(
    (plan) =>
      plan.isActive &&
      amount >= plan.minOrderValue &&
      amount <= plan.maxOrderValue
  );

export const selectTotalActivePlans = (state) =>
  state.branchInstallment.plans.filter((plan) => plan.isActive).length;

export const selectConfigInitialized = (state) =>
  !!state.branchInstallment.config;

export const selectHasPlans = (state) =>
  state.branchInstallment.plans.length > 0;

export const selectHasActivePlans = (state) =>
  state.branchInstallment.plans.some((plan) => plan.isActive);

// Analytics computed selectors
export const selectDefaultRate = (state) => {
  const analytics = state.branchInstallment.analytics;
  if (!analytics?.byStatus) return 0;

  const defaultOrders =
    analytics.byStatus.find((s) => s._id === "DEFAULTED")?.count || 0;
  const totalOrders = analytics.overview?.totalInstallmentOrders || 0;

  return totalOrders > 0 ? (defaultOrders / totalOrders) * 100 : 0;
};

export const selectAverageEMI = (state) =>
  state.branchInstallment.analytics?.overview?.avgEMIAmount || 0;

export const selectTotalRevenue = (state) => {
  const analytics = state.branchInstallment.analytics;
  if (!analytics?.overview) return 0;

  return (
    (analytics.overview.totalInterestEarned || 0) +
    (analytics.overview.totalProcessingFees || 0)
  );
};
